import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { CustomerLayout } from '@/components/CustomerLayout'
import { PortalLoginPage } from '@/routes/login'
import { PortalBillingPage } from '@/routes/billing'
import { PortalLicensesPage } from '@/routes/licenses'
import { PortalUsagePage } from '@/routes/usage'
import { PortalMembersPage } from '@/routes/members'
import { PortalAcceptInvitePage } from '@/routes/accept-invite'
import { hasStoredCustomerSession } from '@/lib/auth/session'

// Standalone portal app (split out of apps-future/console's /portal/*
// subtree, ADR-179 P2 -> portal-standalone-app task). This app IS the
// portal now, so routes are TOP-LEVEL: /login, /billing, /licenses, /usage
// -- no /portal prefix (the console app used one because it shared a route
// tree with the operator shell; there is nothing to disambiguate from
// here). CustomerLayout wraps every route via rootRoute directly (there is
// no sibling operator route tree to keep this isolated from anymore), and
// CustomerAuthProvider wraps the whole app at main.tsx's top level instead
// of being nested mid-tree.
//
// Same programmatic (non-file-based) TanStack Router API as console, for
// the same reason (see apps-future/console/src/router.tsx's comment on why
// file-based codegen was skipped there) -- unchanged here.
const rootRoute = createRootRoute({
  component: () => (
    <CustomerLayout>
      <Outlet />
    </CustomerLayout>
  ),
})

function requireCustomerSession() {
  if (!hasStoredCustomerSession()) {
    throw redirect({ to: '/login' })
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: PortalLoginPage,
})

// acceptInviteRoute is UNAUTHENTICATED (no beforeLoad guard) — a fresh
// invitee has no customer_session cookie yet; this is the whole point of
// the route (ADR-179 P3 / section 7-E).
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: PortalAcceptInvitePage,
})

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  beforeLoad: requireCustomerSession,
  component: PortalBillingPage,
})

const licensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licenses',
  beforeLoad: requireCustomerSession,
  component: PortalLicensesPage,
})

const usageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/usage',
  beforeLoad: requireCustomerSession,
  component: PortalUsagePage,
})

const membersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/members',
  beforeLoad: requireCustomerSession,
  component: PortalMembersPage,
})

// / (root, no segment) redirects to /billing -- the portal's landing view,
// same choice apps-future/console's portalIndexRoute made for /portal.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/billing' })
  },
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  acceptInviteRoute,
  billingRoute,
  licensesRoute,
  usageRoute,
  membersRoute,
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
